import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import MarketingNav from "@/components/app/MarketingNav";
import OtterIcon from "@/components/app/OtterIcon";
import { ArrowRight, Clock } from "lucide-react";

/**
 * ArloSEO public blog index. Pulls from /api/blog/posts (no auth needed)
 * so the page is fully crawlable + indexable.
 */
function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

export default function Blog() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    document.title = "Blog — ArloSEO";
    (async () => {
      try {
        const { data } = await api.get("/blog/posts?limit=50");
        setPosts(data.posts || []);
      } catch {
        setPosts([]);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingNav />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-24" data-testid="blog-page">
        {/* Header */}
        <div className="flex items-end justify-between flex-wrap gap-6 border-b border-border pb-10">
          <div>
            <span className="font-mono-data text-xs uppercase tracking-widest text-muted-foreground">
              // arloseo journal
            </span>
            <h1 className="font-semibold tracking-tight mt-3 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
              Field notes from{" "}
              <span className="bg-gradient-to-r from-primary to-foreground bg-clip-text text-transparent">
                Arlo
              </span>
              .
            </h1>
            <p className="mt-4 max-w-2xl text-base md:text-lg text-muted-foreground leading-relaxed">
              SEO, AI content, and how we build ArloSEO — written (mostly) by Arlo, edited by humans.
            </p>
          </div>
          <div className="flex items-center gap-2 font-mono-data text-[11px] uppercase tracking-widest text-muted-foreground">
            <OtterIcon size={18} className="text-foreground" />
            <span>powered by the Content Studio</span>
          </div>
        </div>

        {/* List */}
        {posts === null && (
          <div className="mt-12 text-sm text-muted-foreground" data-testid="blog-loading">Loading…</div>
        )}
        {posts && posts.length === 0 && (
          <div className="mt-16 border border-dashed border-foreground/30 p-12 text-center" data-testid="blog-empty">
            <div className="w-14 h-14 mx-auto bg-foreground grid place-items-center text-background mb-4">
              <OtterIcon size={36} />
            </div>
            <h3 className="font-display tracking-tight text-2xl">Arlo&apos;s drafting his first post.</h3>
            <p className="text-sm text-muted-foreground mt-1">Subscribe to find out when the first one drops.</p>
          </div>
        )}
        {posts && posts.length > 0 && (
          <ul className="mt-12 divide-y divide-border" data-testid="blog-list">
            {posts.map((p, i) => (
              <li key={p.id} className="py-8 first:pt-0" data-testid={`blog-post-${i}`}>
                <Link to={`/blog/${p.slug}`} className="block group">
                  <div className="flex items-center gap-3 font-mono-data text-[10px] uppercase tracking-widest text-muted-foreground">
                    {p.target_keyword && (
                      <span className="border border-border bg-secondary/50 px-1.5 py-0.5">{p.target_keyword}</span>
                    )}
                    <span>{timeAgo(p.published_at)}</span>
                    <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {p.reading_minutes} min</span>
                  </div>
                  <h2 className="font-semibold tracking-tight mt-3 text-2xl sm:text-3xl group-hover:underline underline-offset-4 decoration-2">
                    {p.title}
                  </h2>
                  {p.excerpt && (
                    <p className="mt-3 text-muted-foreground leading-relaxed line-clamp-2">{p.excerpt}</p>
                  )}
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium">
                    Read post <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
